import { useState, useEffect } from 'react';
import { AccountLayout } from '@/components/AccountLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { requireUser } from '@/lib/requireUser';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ProAvatar } from '@/components/ui/ProAvatar';
import { Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { getProfileAvatar, updateProfileAvatar } from '@/data/profile';
import { useAgentProfile } from '@/hooks/useAgentProfile';

interface AgentGoals {
  volume_target: number;
  business_mix: 'buyers' | 'sellers' | 'balanced' | 'investors';
  niche: string[];
  lead_strategy: 'referral' | 'leads' | 'sphere' | 'team' | 'mixed';
  work_style: 'go_getter' | 'relationship' | 'analytical' | 'team_player';
  growth_path: 'stay_solo' | 'build_team' | 'join_team' | 'scale_existing';
}

const Profile = () => {
  const { user, isProMember, membership } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile, updateProfile, isUpdating } = useAgentProfile();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [goals, setGoals] = useState<AgentGoals>({
    volume_target: 10,
    business_mix: 'balanced',
    niche: [],
    lead_strategy: 'referral',
    work_style: 'relationship',
    growth_path: 'stay_solo'
  });
  const isFounder = membership?.tier === 'founder';

  useEffect(() => {
    requireUser({ user, navigate, currentPath: '/account/profile' });
    
    // Load current avatar
    const loadAvatar = async () => {
      if (!user?.id) return;
      
      try {
        const url = await getProfileAvatar(user.id);
        if (url) setAvatarUrl(url);
      } catch (error) {
        console.error('Error loading avatar:', error);
      }
    };
    
    loadAvatar();
  }, [user, navigate]);

  // Load current goals from profile
  useEffect(() => {
    if (profile?.goals && Array.isArray(profile.goals) && profile.goals.length > 0) {
      const profileGoals = profile.goals[0];
      if (typeof profileGoals === 'object' && profileGoals !== null) {
        setGoals(profileGoals as AgentGoals);
      }
    }
  }, [profile]);

  const handleSaveGoals = async () => {
    try {
      await updateProfile({ goals: [goals] });
      toast.success('Goals saved successfully!');
    } catch (error) {
      toast.error('Failed to save goals');
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
    
    if (error) {
      toast.error('Failed to send reset email');
    } else {
      toast.success('Password reset email sent!');
    }
    setLoading(false);
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploading(true);

    try {
      const result = await updateProfileAvatar(user.id, file);
      setAvatarUrl(result.url);
      toast.success('Avatar updated successfully!');
      
      // Invalidate auth data to update header avatar
      queryClient.invalidateQueries({ queryKey: ['auth-data', user.id] });
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error(error.message || 'Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  return (
    <AccountLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profile Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your account settings and preferences</p>
        </div>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>Your basic account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="flex items-center gap-2">
                <Input 
                  id="email" 
                  type="email" 
                  value={user.email || ''} 
                  disabled 
                  className="bg-muted"
                />
                {user.email_confirmed_at && (
                  <Badge variant="secondary" className="shrink-0">Verified</Badge>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-id">User ID</Label>
              <Input 
                id="user-id" 
                value={user.id} 
                disabled 
                className="bg-muted font-mono text-xs"
              />
            </div>

            <div className="pt-4">
              <Button 
                onClick={handlePasswordReset}
                disabled={loading}
                variant="outline"
              >
                {loading ? 'Sending...' : 'Reset Password'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Public Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Public Profile</CardTitle>
            <CardDescription>Information visible to other users</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar Upload */}
            <div className="space-y-4">
              <Label>Profile Picture</Label>
              <div className="flex items-center gap-6">
                <ProAvatar
                  avatarUrl={avatarUrl}
                  email={user.email}
                  isPro={isProMember}
                  isFounder={isFounder}
                  size="lg"
                />
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={uploading}
                      onClick={() => document.getElementById('avatar-upload')?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Picture'}
                    </Button>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarUpload}
                      disabled={uploading}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG or WEBP. Max 2MB.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="display-name">Display Name</Label>
              <Input 
                id="display-name" 
                placeholder="Enter your display name" 
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Input 
                id="bio" 
                placeholder="Tell us about yourself" 
              />
            </div>

            <Button disabled>Save Changes</Button>
          </CardContent>
        </Card>

        {/* Goals Section */}
        <Card>
          <CardHeader>
            <CardTitle>Your Business Goals</CardTitle>
            <CardDescription>Define your goals so we can recommend the perfect tools</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Volume Target */}
            <div className="space-y-2">
              <Label>Annual deal target: {goals.volume_target} deals</Label>
              <Input
                type="range"
                min={1}
                max={100}
                value={goals.volume_target}
                onChange={e => setGoals({ ...goals, volume_target: parseInt(e.target.value) })}
              />
            </div>

            {/* Business Mix */}
            <div className="space-y-2">
              <Label>Buyer/Seller Split</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'buyers', label: 'Mostly Buyers' },
                  { value: 'sellers', label: 'Mostly Sellers' },
                  { value: 'balanced', label: '50/50 Balanced' },
                  { value: 'investors', label: 'Investors' },
                ].map(mix => (
                  <Button
                    key={mix.value}
                    type="button"
                    variant={goals.business_mix === mix.value ? 'default' : 'outline'}
                    onClick={() => setGoals({ ...goals, business_mix: mix.value as any })}
                    size="sm"
                  >
                    {mix.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Niche */}
            <div className="space-y-2">
              <Label>Focus Areas (select all that apply)</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'luxury', label: 'Luxury' },
                  { value: 'investment', label: 'Investment' },
                  { value: 'new_construction', label: 'New Construction' },
                  { value: 'first_time', label: 'First-Time Buyers' },
                  { value: 'relocation', label: 'Relocation' },
                ].map(niche => (
                  <Button
                    key={niche.value}
                    type="button"
                    variant={goals.niche.includes(niche.value) ? 'default' : 'outline'}
                    onClick={() => {
                      const newNiches = goals.niche.includes(niche.value)
                        ? goals.niche.filter(n => n !== niche.value)
                        : [...goals.niche, niche.value];
                      setGoals({ ...goals, niche: newNiches });
                    }}
                    size="sm"
                  >
                    {niche.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Lead Strategy */}
            <div className="space-y-2">
              <Label>Lead Strategy</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'referral', label: 'Referrals' },
                  { value: 'leads', label: 'Paid Leads' },
                  { value: 'sphere', label: 'Sphere' },
                  { value: 'team', label: 'Team' },
                ].map(strategy => (
                  <Button
                    key={strategy.value}
                    type="button"
                    variant={goals.lead_strategy === strategy.value ? 'default' : 'outline'}
                    onClick={() => setGoals({ ...goals, lead_strategy: strategy.value as any })}
                    size="sm"
                  >
                    {strategy.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Work Style */}
            <div className="space-y-2">
              <Label>Work Style</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'go_getter', label: 'Hustler' },
                  { value: 'relationship', label: 'Relationship' },
                  { value: 'analytical', label: 'Systems' },
                  { value: 'team_player', label: 'Team Player' },
                ].map(style => (
                  <Button
                    key={style.value}
                    type="button"
                    variant={goals.work_style === style.value ? 'default' : 'outline'}
                    onClick={() => setGoals({ ...goals, work_style: style.value as any })}
                    size="sm"
                  >
                    {style.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Growth Path */}
            <div className="space-y-2">
              <Label>Growth Path</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'stay_solo', label: 'Stay Solo' },
                  { value: 'build_team', label: 'Build Team' },
                  { value: 'join_team', label: 'Join Team' },
                  { value: 'scale_existing', label: 'Scale Team' },
                ].map(path => (
                  <Button
                    key={path.value}
                    type="button"
                    variant={goals.growth_path === path.value ? 'default' : 'outline'}
                    onClick={() => setGoals({ ...goals, growth_path: path.value as any })}
                    size="sm"
                  >
                    {path.label}
                  </Button>
                ))}
              </div>
            </div>

            <Button onClick={handleSaveGoals} disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save Goals'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AccountLayout>
  );
};

export default Profile;
